import * as React from "react";
import {
  Card,
  List,
  Typography,
  CardContent,
  ListItem,
  makeStyles,
  createStyles,
  Theme,
  Tooltip,
} from "@material-ui/core";
import InstallationModel from "../../core/models/Installation";
import TankSVG from "../../assets/diagrams/tank";
import Skeleton from "@material-ui/lab/Skeleton";
import { NullableNumber, NullableString } from "../../core/models/ModelHelpers";
import CustomerModel from "../../core/models/Customer";
import {
  calculateLevelPercentage,
  checkForLevelDataDelay,
  getLevelColor,
  getLevelStatus,
} from "../../helpers/installationTankHelpers";
import { isNumberValid, formatValue } from "../../helpers/utility";
import InfoIcon from "@material-ui/icons/Help";
import {
  OUTAGE_LEVEL_HELP_TEXT,
  RESERVE_LEVEL_HELP_TEXT,
} from "../../core/constants/tooltipHelpersText";
import clsx from "clsx";
import InstallationGroup from "../../core/models/InstallationGroup";
import User from "../../core/models/User";
import { SessionCache } from "../../helpers/cache";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "color": theme.customPalette.text.primary,

      "& .MuiCardContent-root:last-child": {
        paddingBottom: "unset",
      },

      "& .MuiCardContent-root": {
        padding: "unset",
      },

      "& .label": {
        fontSize: "0.7rem",
        maxWidth: "55%",
        flex: 1,
      },

      "& .value": {
        fontSize: "1rem",
        flex: 1,
        textAlign: "end",
        fontWeight: "bold",
      },

      "& .name": {
        fontSize: "0.8rem",
        maxWidth: 150,
        fontWeight: "bold",
        flex: 1,
        textAlign: "end",
      },
    },

    flexColumn: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },

    headerWrapper: {
      "padding": theme.spacing(1),

      "& .header": {
        color: theme.palette.primary.main,
        textAlign: "center",
        borderBottom: "2px dotted",
      },

      "& .tankWrapper": {
        "flex": 1,
        "marginTop": theme.spacing(2),

        "& svg": {
          width: "auto",
          height: "auto",
          maxHeight: 240,
          // marginLeft: -33,
        },

        "& .color-text": {
          color: theme.palette.primary.main,
          textAlign: "center",
          fontSize: "1.2rem",
        },

        "& .text-small": {
          fontSize: "0.7rem",
        },

        "& .text-large": {
          fontSize: "1.5rem",
        },
      },
    },

    listRoot: {
      "width": "100%",
      "paddingBottom": "unset",
      "paddingTop": "unset",

      "& .MuiListItem-gutters": {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },

      "& li": {
        "justifyContent": "space-between",

        "&:nth-child(even)": {
          background: theme.customPaper.background.cardBackgroundLight,
        },
        "&:nth-child(odd)": {
          background: theme.palette.background.default,
        },
      },
    },

    skeleton: {
      flex: 1,
    },

    infoIcon: {
      "marginLeft": theme.spacing(1),

      "&.hidden-icon": {
        color: "transparent",
      },
    },
  })
);

type Props = {
  installation?: InstallationModel;
  customer?: CustomerModel;
  isLoading?: boolean;
};

function InstallationInformation(props: Props) {
  const classes = useStyles();
  const { installation, customer, isLoading = false } = props;

  const getUOM = (uom: NullableString) => {
    return uom ? `(${uom})` : "";
  };

  const getPossiblyEmptyMessage = (message: NullableString | NullableNumber) => {
    return message ? message : "-";
  };

  const loadableMessage = (message: NullableString | NullableNumber) => {
    return isLoading ? (
      <span className={classes.skeleton}>
        <Skeleton />
      </span>
    ) : (
      getPossiblyEmptyMessage(message)
    );
  };

  const StorageCache = new SessionCache();

  const LoggedUser: User | null = StorageCache.get("user");

  const groups: InstallationGroup[] | undefined = LoggedUser?.installationGroup ?? [];

  const radixInstallation = groups
    ?.find((e) => e.id == customer?.customer_id)
    ?.installationList.find((e) => e.installationId == installation?.installation_id);

  const thresholds = {
    veryLow: installation?.outage_level_su,
    low: installation?.reserve_level_su,
  };

  const autonomyOutage = React.useMemo(() => {
    return installation?.autonomy_hours_to_outage
      ? Number(installation?.autonomy_hours_to_outage)
      : 0;
  }, [installation]);

  return (
    <Card className={classes.root}>
      <CardContent className={classes.flexColumn}>
        {/* Card Header */}

        {/* Tank SVG */}
        <div className={classes.headerWrapper}>
          <Typography className="header" component="h3">
            Informações da Instalação
          </Typography>
          <div className="tankWrapper">
            <TankSVG
              showOutsideLabel
              opaque={
                installation?.last_rtu_reading_date
                  ? checkForLevelDataDelay(installation?.last_rtu_reading_date)
                  : true
              }
              value={installation?.installation_level}
              capacity={installation?.inventory_capacity_su}
              status={installation?.installation_level_status}
              thresholds={{
                low: installation?.reserve_level_su,
                veryLow: installation?.outage_level_su,
              }}
            />

            <Typography className="color-text">
              <strong>{loadableMessage(installation?.product_code)}</strong>
            </Typography>

            <div className={classes.flexColumn}>
              <Typography className="text-small">Número White</Typography>
              <Typography className="text-large">
                <strong>{loadableMessage(installation?.installation_id)}</strong>
              </Typography>
            </div>
          </div>
        </div>

        {/* Information Display */}
        <List className={classes.listRoot}>
          <ListItem>
            <span className="label">Nome Fantasia</span>
            <span className="name">
              {loadableMessage(radixInstallation?.name || installation?.name)}
            </span>

            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">Nível Atual</span>
            <span className="value">
              {loadableMessage(
                installation?.installation_level !== undefined &&
                  installation?.inventory_capacity_su !== undefined
                  ? `${calculateLevelPercentage(
                      installation?.installation_level,
                      installation?.inventory_capacity_su
                    )}%`
                  : "-"
              )}
            </span>
            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">Nome do Grupo</span>
            <span className="name">{loadableMessage(customer?.customer_name)}</span>
            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">Tipo de Produto</span>
            <span className="value">{loadableMessage(installation?.product_code)}</span>
            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">Status</span>
            <span
              className="value"
              style={{
                color:
                  installation?.installation_level_status ||
                  (isNumberValid(installation?.installation_level) &&
                    isNumberValid(thresholds.veryLow))
                    ? getLevelColor(
                        installation?.installation_level_status || installation?.installation_level,
                        thresholds
                      )
                    : "none",
              }}
            >
              {loadableMessage(
                installation?.installation_level_status ||
                  getLevelStatus(installation?.installation_level, thresholds)
              )}
            </span>
            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">{`Capacidade ${getUOM(installation?.product_uom)}`}</span>
            <span className="value">
              {loadableMessage(formatValue(installation?.inventory_capacity_su))}
            </span>

            <InfoIcon className={clsx(classes.infoIcon, "hidden-icon")} fontSize="small" />
          </ListItem>

          <ListItem>
            <span className="label">{`Estoque de Segurança ${getUOM(
              installation?.product_uom
            )}`}</span>
            <span className="value">
              {loadableMessage(formatValue(installation?.reserve_level_su))}
            </span>

            <Tooltip title={RESERVE_LEVEL_HELP_TEXT}>
              <InfoIcon className={classes.infoIcon} fontSize="small" color="primary" />
            </Tooltip>
          </ListItem>

          <ListItem>
            <span className="label">{`Lastro${
              installation?.product_uom ? ` (${installation?.product_uom})` : ""
            }`}</span>
            <span className="value">
              {loadableMessage(formatValue(installation?.outage_level_su))}
            </span>

            <Tooltip title={OUTAGE_LEVEL_HELP_TEXT}>
              <InfoIcon className={classes.infoIcon} fontSize="small" color="primary" />
            </Tooltip>
          </ListItem>

          <ListItem>
            <span className="label">{`Autonomia Estimada`}</span>
            <span className="value">
              {autonomyOutage == 360 ? ">" : ""}
              {formatValue(autonomyOutage)} h
            </span>

            <Tooltip title="Total de horas/dias previstas até que acabe o produto no tanque, com base no histórico de consumo recente">
              <InfoIcon className={classes.infoIcon} fontSize="small" color="primary" />
            </Tooltip>
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
}

export default InstallationInformation;
